/**
 * ActiveLearnerBar.jsx — Phase 1.3 + Phase 4.
 *
 * A single, persistent context strip mounted on every grown-up surface.
 * It binds to the ONE active-learner source (getActiveChild, written by
 * the header switcher) — never a hardcoded or first-child fallback — and
 * shows the current week of the 36-week plan.
 *
 *   With a learner : "<Name> · Week <n> of 36"
 *   No learner     : "No learner selected — choose one"  (→ /family)
 *
 * It also renders the persistent role label ("Parent view" / "Teacher
 * view" / "School view") and a "← Back to my child's screen" link so no
 * grown-up page is a dead end.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, UserRound } from "lucide-react";
import { getActiveChild } from "@/lib/children";
import { loadState } from "@/lib/learningPlan";

const TOTAL_WEEKS = 36;

function currentWeekFor(childId) {
  try {
    const s = loadState();
    const w = s?.plans?.[childId]?.weekNumber;
    return Math.max(1, Math.min(TOTAL_WEEKS, w || 1));
  } catch {
    return 1;
  }
}

/**
 * @param {"parent"|"teacher"|"school"} role
 */
export default function ActiveLearnerBar({ role = "parent" }) {
  const child = getActiveChild();
  const roleLabel =
    role === "teacher" ? "Teacher view" : role === "school" ? "School view" : "Parent view";

  return (
    <div
      data-testid="active-learner-bar"
      className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border-2 border-slate-900 bg-white px-3 py-2 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex items-center gap-1 rounded-full border-2 border-slate-900 bg-amber-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-slate-900">
          {roleLabel}
        </span>
        {child ? (
          <span
            data-testid="active-learner-label"
            className="inline-flex items-center gap-1.5 font-bold text-[13px] text-slate-800 min-w-0"
          >
            <UserRound size={14} strokeWidth={3} className="shrink-0" />
            <span className="truncate">{child.name || "Learner"}</span>
            <span className="text-slate-400">·</span>
            <span className="whitespace-nowrap">Week {currentWeekFor(child.id)} of {TOTAL_WEEKS}</span>
          </span>
        ) : (
          <Link
            to="/family"
            data-testid="active-learner-choose"
            className="inline-flex items-center gap-1.5 font-bold text-[13px] text-rose-700 underline decoration-2 underline-offset-2 hover:text-rose-900"
          >
            No learner selected — choose one
          </Link>
        )}
      </div>
      <Link
        to="/"
        data-testid="back-to-child-screen"
        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border-2 border-slate-900 bg-white font-bold text-[11px] shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
      >
        <ArrowLeft size={12} strokeWidth={3} /> Back to my child&rsquo;s screen
      </Link>
    </div>
  );
}
